import { useState } from 'react';
import {
  User as UserIcon,
  Settings,
  BookOpen,
  LogOut,
  ChevronRight,
  ShieldCheck,
} from 'lucide-react';
import { useRedirect } from '../../Shared/Hooks/useRedirect ';
import { logoutUser } from '../../Shared/Services/ApiClient';
import { UserForm } from '../Types/UsersType';
import openApiDocs from '../../Shared/Utils/openApiDocs';
import { storage } from '../../Shared/Utils/authStorage';

interface ProfileDropdownProps {
  candidate: UserForm | null;
}

interface RoleMeta {
  label: string;
  profilePath?: string;
  settingsPath?: string;
}

const ROLE_META: Record<string, RoleMeta> = {
  CANDIDATE: {
    label: 'Candidate',
    profilePath: '/candidate/edit-profile',
    settingsPath: '/candidate/settings',
  },
  RECRUITER: {
    label: 'Recruiter',
    profilePath: '/recruiter/profile',
    settingsPath: '/recruiter/settings',
  },
  ADMIN: {
    label: 'Admin',
    profilePath: '/admin/profile',
    settingsPath: '/admin/settings',
  },
  SUPER_ADMIN: {
    label: 'Super Admin',
    profilePath: '/admin/profile',
    settingsPath: '/admin/settings',
  },
};

export default function ProfileDropdown({ candidate }: ProfileDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const redirect = useRedirect();

  const role = (storage.getRole() || candidate?.role || '').toUpperCase();
  const isAdmin = role === 'ADMIN' || role === 'SUPER_ADMIN';
  const meta: RoleMeta = ROLE_META[role] ?? { label: role || 'Member' };

  const fullName =
    `${candidate?.firstName ?? ''} ${candidate?.lastName ?? ''}`.trim() || 'Nexgent User';
  const initials =
    `${candidate?.firstName?.[0] ?? ''}${candidate?.lastName?.[0] ?? ''}`.toUpperCase() || 'N';
  const isActive = candidate
    ? Boolean(candidate.isVerified || candidate.status?.toUpperCase() === 'ACTIVE')
    : true;

  const go = (path?: string) => {
    setIsOpen(false);
    if (path) redirect(path);
  };

  const itemClass =
    'w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-pink-50/70 hover:text-pink-600 rounded-xl transition-all duration-200 group';
  const iconWrapClass =
    'flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-gray-50 text-gray-500 transition-colors group-hover:bg-pink-100/60 group-hover:text-pink-600';

  return (
    <div className="relative inline-block">
      {/* Trigger */}
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-label="Open profile menu"
        aria-expanded={isOpen}
        className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-pink-500 to-indigo-600 text-xs font-black text-white ring-2 ring-white/20 shadow-xl transition-all hover:ring-pink-400/60"
      >
        {initials}
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />

          <div className="absolute right-0 z-20 mt-3 w-72 origin-top-right overflow-hidden rounded-2xl border border-gray-100 bg-white/95 shadow-[0_20px_50px_rgb(0,0,0,0.15)] backdrop-blur-md animate-in fade-in-50 slide-in-from-top-3 duration-200 ease-out">
            {/* Identity header */}
            <div className="relative border-b border-gray-100 bg-gradient-to-br from-gray-50 to-white px-4 py-4">
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-pink-500 to-indigo-600 text-sm font-black text-white shadow-md">
                  {initials}
                </span>
                <div className="min-w-0">
                  <p className="truncate text-sm font-bold text-gray-900">{fullName}</p>
                  <p className="truncate text-xs text-gray-500">
                    {candidate?.email || '—'}
                  </p>
                </div>
              </div>

              <div className="mt-3 flex items-center gap-2">
                <span className="inline-flex items-center rounded-full bg-indigo-50 px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wider text-indigo-600">
                  {meta.label}
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-600">
                  <span
                    className={`h-1.5 w-1.5 rounded-full ${
                      isActive ? 'bg-emerald-500 animate-pulse' : 'bg-gray-400'
                    }`}
                  />
                  {isActive ? 'Active Account' : 'Inactive'}
                </span>
              </div>
            </div>

            {/* Menu */}
            <div className="space-y-0.5 px-2 py-2">
              {meta.profilePath && (
                <button type="button" className={itemClass} onClick={() => go(meta.profilePath)}>
                  <span className={iconWrapClass}>
                    <UserIcon size={16} />
                  </span>
                  <span className="flex-1 text-left">View Profile</span>
                  <ChevronRight size={15} className="text-gray-300 group-hover:text-pink-400" />
                </button>
              )}

              {meta.settingsPath && (
                <button type="button" className={itemClass} onClick={() => go(meta.settingsPath)}>
                  <span className={iconWrapClass}>
                    <Settings size={16} />
                  </span>
                  <span className="flex-1 text-left">Account Settings</span>
                  <ChevronRight size={15} className="text-gray-300 group-hover:text-pink-400" />
                </button>
              )}

              {isAdmin && (
                <button
                  type="button"
                  className={itemClass}
                  onClick={() => {
                    setIsOpen(false);
                    openApiDocs();
                  }}
                >
                  <span className={iconWrapClass}>
                    <BookOpen size={16} />
                  </span>
                  <span className="flex-1 text-left">API Docs</span>
                </button>
              )}
            </div>

            <div className="border-t border-gray-100" />

            {/* Logout */}
            <div className="px-2 py-2">
              <button
                type="button"
                onClick={logoutUser}
                className="group flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold text-red-600 transition-all duration-200 hover:bg-red-50"
              >
                <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-red-50 text-red-500 transition-colors group-hover:bg-red-100/70">
                  <LogOut size={16} />
                </span>
                <span className="flex-1 text-left">Log out</span>
              </button>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-center gap-1.5 border-t border-gray-100 bg-gray-50/60 py-2 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
              <ShieldCheck size={11} /> Secured by Nexgent
            </div>
          </div>
        </>
      )}
    </div>
  );
}
